import {
  autoUpdate,
  flip,
  offset,
  type Placement,
  shift,
  useFloating,
} from '@floating-ui/react';
import {
  emojiFromShortcodes,
  MENTION_GAP_MARK,
} from '@littlebird/action-cards';
import { getLogger } from '@littlebird/logger';
import {
  type Editor,
  Extension,
  InputRule,
  type JSONContent,
  Mark,
  mergeAttributes,
  Node,
} from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import {
  NodeViewWrapper,
  type NodeViewProps,
  ReactNodeViewRenderer,
  ReactRenderer,
} from '@tiptap/react';
import Suggestion, {
  type SuggestionKeyDownProps,
  type SuggestionProps,
} from '@tiptap/suggestion';
import { sortBy, uniq, upperFirst } from 'lodash';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { AppLogo } from '@/assets/icons/AppLogo';
import { cn } from '@/lib/utils';

import { searchEmoji } from './emoji-search';
import {
  MentionChipContent,
  mentionChipClassName,
  mentionChipTitle,
} from './mention-chip';

const log = getLogger('mention');

export type MentionItem = {
  /** Stable within a source. Combined with `kind` for document-wide identity. */
  id: string;
  /** Identifies the source that owns this mention, such as `app`. */
  kind: string;
  name: string;
  /** Picker-only display name when `name` is an identifier the user types, such as a skill slug */
  label?: string;
  /** Picker-only second line, such as a person's emails, shown in place of `detail`. The query matches it unless `isSubtitleSearchable` is false; the chip doesn't store it. */
  subtitle?: string;
  isSubtitleSearchable?: boolean;
  /** Picker-only: the Littlebird icon shows beside the name of someone who has a Littlebird account and shares with the user */
  isOnLittlebird?: boolean;
  iconUrl?: string | null;
  detail?: string | null;
  data?: Record<string, unknown>;
  /** Used by Hummingbird for context that it inserted automatically. */
  autoIncluded?: boolean;
  /**
   * Picker-only heading this item groups under, for sources that split their
   * items across sections. Defaults to the source title.
   */
  section?: string;
};

export type MentionSource = {
  kind: string;
  title: string;
  listItems: (editor: Editor) => Promise<MentionItem[]>;
  /** Caps how many of this source's items the picker shows, after the query filter and in `listItems` order. */
  maxItems?: number;
  renderIcon?: (mention: MentionItem, surface: 'chip' | 'menu') => ReactNode;
  onSelect?: (mention: MentionItem, editor: Editor) => void;
  onContextMenu?: (
    mention: MentionItem,
    event: ReactMouseEvent<HTMLElement>
  ) => void;
};

type MentionOptions = {
  sources: MentionSource[];
};

type MentionSuggestionOptions = MentionOptions & {
  /** Extra props merged onto the floating menu container (e.g. Hummingbird's
   *  click-through mouse capture on the transparent overlay). */
  menuProps?: Record<string, unknown>;
};

const mentionFromNode = (node: NodeViewProps['node']): MentionItem => ({
  id: node.attrs.id,
  kind: node.attrs.kind,
  name: node.attrs.name,
  iconUrl: node.attrs.iconUrl,
  detail: node.attrs.detail,
  data: node.attrs.data,
  autoIncluded: node.attrs.autoIncluded,
});

const MentionChip = ({ node, selected, extension }: NodeViewProps) => {
  const mention = mentionFromNode(node);
  const source = (extension.options as MentionOptions).sources.find(
    ({ kind }) => kind === mention.kind
  );
  const icon = source?.renderIcon?.(mention, 'chip');

  return (
    <NodeViewWrapper
      as="span"
      contentEditable={false}
      title={mentionChipTitle(mention)}
      onContextMenu={(event: ReactMouseEvent<HTMLElement>) =>
        source?.onContextMenu?.(mention, event)
      }
      className={mentionChipClassName(selected)}
    >
      <MentionChipContent mention={mention} icon={icon} />
    </NodeViewWrapper>
  );
};

export const Mention = Node.create<MentionOptions>({
  name: 'mention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addOptions: () => ({ sources: [] }),
  addAttributes: () => ({
    id: { default: '' },
    kind: { default: '' },
    name: { default: '' },
    iconUrl: { default: null },
    detail: { default: null },
    data: { default: null, rendered: false },
    autoIncluded: { default: false, rendered: false },
  }),
  parseHTML: () => [{ tag: 'span[data-mention]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'span',
    mergeAttributes(HTMLAttributes, { 'data-mention': '' }),
  ],
  renderText({ node }) {
    return `@${node.attrs.name}`;
  },
  addStorage: () => ({
    markdown: {
      serialize: (
        state: { text: (text: string) => void },
        node: NodeViewProps['node']
      ) => state.text(`@${node.attrs.name}`),
    },
  }),
  addNodeView: () => ReactNodeViewRenderer(MentionChip),
});

// Trailing space after a mention chip. Kept for caret/typing UX; selection
// highlight is suppressed so Cmd+A doesn't show blue bars between chips.
export const MentionGap = Mark.create({
  name: MENTION_GAP_MARK,
  inclusive: false,
  parseHTML: () => [{ tag: 'span[data-mention-gap]' }],
  renderHTML: () => [
    'span',
    { 'data-mention-gap': '', class: 'mention-gap' },
    0,
  ],
  addStorage: () => ({
    markdown: { serialize: { open: '', close: '' } },
  }),
});

export const mentionTrailingSpace: JSONContent = {
  type: 'text',
  text: ' ',
  marks: [{ type: MentionGap.name }],
};

export const removeMention = (
  editor: Editor,
  { kind, id }: Pick<MentionItem, 'kind' | 'id'>
) => {
  const { state } = editor;
  const ranges: { from: number; to: number }[] = [];
  state.doc.descendants((node, pos) => {
    if (
      node.type.name !== Mention.name ||
      node.attrs.kind !== kind ||
      node.attrs.id !== id
    )
      return;
    const chipEnd = pos + node.nodeSize;
    const nodeAfter = state.doc.resolve(chipEnd).nodeAfter;
    const hasTrailingGap =
      nodeAfter?.isText === true &&
      nodeAfter.marks.some((mark) => mark.type.name === MentionGap.name);
    ranges.push({ from: pos, to: hasTrailingGap ? chipEnd + 1 : chipEnd });
  });
  if (ranges.length === 0) return;
  const { tr } = state;
  ranges.reverse().forEach(({ from, to }) => tr.delete(from, to));
  editor.view.dispatch(tr);
};

/** The structured mentions currently in the editor, in document order. */
export const collectMentions = (editor: Editor): MentionItem[] => {
  const mentions: MentionItem[] = [];
  editor.state.doc.descendants((node) => {
    if (node.type.name !== Mention.name) return;
    mentions.push(mentionFromNode(node));
  });
  return mentions;
};

// Mention chips are inline atoms; the default Backspace selects the atom
// (leaving the caret on a dangling line) and only deletes on a second press.
// Delete the chip — and the trailing space it's inserted with — in one press
// when the caret sits right after it.
export const MentionKeymap = Extension.create({
  name: 'mentionKeymap',
  addKeyboardShortcuts() {
    /* eslint-disable littlebird/naming-convention -- TipTap shortcut keys use keyboard names */
    return {
      Backspace: () => {
        const { state } = this.editor;
        const { selection } = state;
        if (!selection.empty) return false;
        const { $from } = selection;
        const before = $from.nodeBefore;
        if (before?.type.name === Mention.name) {
          return this.editor.commands.deleteRange({
            from: $from.pos - before.nodeSize,
            to: $from.pos,
          });
        }
        if (before?.isText && before.text === ' ') {
          const spaceStart = $from.pos - before.nodeSize;
          const chip = state.doc.resolve(spaceStart).nodeBefore;
          if (chip?.type.name === Mention.name) {
            return this.editor.commands.deleteRange({
              from: spaceStart - chip.nodeSize,
              to: $from.pos,
            });
          }
        }
        return false;
      },
    };
    /* eslint-enable littlebird/naming-convention */
  },
});

type MentionSuggestionItem = {
  mention: MentionItem;
  source: MentionSource;
};

/** Headings appear whenever this changes, so a section's items must be adjacent. */
const sectionTitleOf = ({ mention, source }: MentionSuggestionItem): string =>
  mention.section ?? source.title;

type SuggestionListHandle = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

/** `mention` is the `@` chip picker; `slash` is the command-palette style `/` picker */
type PickerVariant = 'mention' | 'slash';

type SuggestionListProps = {
  items: MentionSuggestionItem[];
  command: (item: MentionSuggestionItem) => void;
  clientRect?: (() => DOMRect | null) | null;
  menuProps?: Record<string, unknown>;
  query: string;
  variant: PickerVariant;
};

const highlightMatch = (label: string, query: string): ReactNode => {
  const start = query ? label.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (start < 0) return <span className="text-foreground">{label}</span>;
  const end = start + query.length;
  return (
    <span className="text-foreground-muted">
      {label.slice(0, start)}
      <span className="text-foreground">{label.slice(start, end)}</span>
      {label.slice(end)}
    </span>
  );
};

const LittlebirdMark = () => (
  <AppLogo
    size={12}
    customColor="currentColor"
    decorative
    className="shrink-0 text-foreground-muted"
  />
);

const MentionSuggestionList = forwardRef<
  SuggestionListHandle,
  SuggestionListProps
>(({ items, command, clientRect, menuProps, query, variant }, ref) => {
  const [index, setIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [placement, setPlacement] = useState<Placement>('top-start');
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const show = !dismissed && items.length > 0;

  const {
    refs,
    floatingStyles,
    placement: resolvedPlacement,
    isPositioned,
  } = useFloating({
    open: show,
    placement,
    strategy: 'fixed',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
  });

  useEffect(() => {
    if (isPositioned && resolvedPlacement !== placement)
      setPlacement(resolvedPlacement);
  }, [isPositioned, placement, resolvedPlacement]);

  useEffect(() => {
    if (!clientRect) return;
    refs.setReference({
      getBoundingClientRect: () => clientRect() ?? new DOMRect(0, 0, 0, 0),
    });
  }, [clientRect, refs, items]);

  useEffect(() => {
    setIndex(0);
    setDismissed(false);
  }, [items]);

  useEffect(() => {
    optionRefs.current[index]?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'Escape') {
        setDismissed(true);
        return true;
      }
      if (dismissed || items.length === 0) return false;
      if (event.key === 'ArrowUp') {
        setIndex((current) => (current - 1 + items.length) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setIndex((current) => (current + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.stopImmediatePropagation();
        const item = items[index];
        if (item) command(item);
        return true;
      }
      return false;
    },
  }));

  const anchorRect = clientRect?.();
  if (!show || !anchorRect) return null;

  return createPortal(
    <div
      {...menuProps}
      ref={refs.setFloating}
      className={cn(
        'z-[9999] overflow-hidden rounded-2xl border border-grayA-6 p-1.5 shadow-lg',
        variant === 'mention' && 'w-72'
      )}
      style={{
        ...floatingStyles,
        backgroundColor: 'var(--surface-default)',
        // The slash picker spans the composer it sits above, like a command palette
        width: variant === 'slash' ? anchorRect.width : undefined,
      }}
    >
      <div className="max-h-80 overflow-y-auto text-sm.5 scrollbar-hide">
        {items.map((item, itemIndex) => {
          const { mention, source } = item;
          const sectionTitle = sectionTitleOf(item);
          const showTitle =
            itemIndex === 0 ||
            sectionTitleOf(items[itemIndex - 1]) !== sectionTitle;
          const icon = source.renderIcon?.(mention, 'menu');
          return (
            <div key={`${mention.kind}:${mention.id}`}>
              {showTitle && (
                <div className="px-2.5 py-1.5 text-sm text-foreground-muted">
                  {sectionTitle}
                </div>
              )}
              <button
                ref={(element) => {
                  optionRefs.current[itemIndex] = element;
                }}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  command({ mention, source });
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left transition-colors',
                  itemIndex === index
                    ? 'bg-grayA-4 text-foreground'
                    : 'text-foreground-muted hover:bg-grayA-3'
                )}
              >
                {icon ??
                  (mention.iconUrl ? (
                    <img
                      width={16}
                      height={16}
                      src={mention.iconUrl}
                      alt=""
                      className="rounded-sm"
                    />
                  ) : (
                    <span className="size-5 flex-shrink-0 rounded-sm bg-grayA-5" />
                  ))}
                {variant === 'slash' ? (
                  <>
                    <span className="flex-shrink-0 truncate font-medium">
                      {highlightMatch(mention.label ?? mention.name, query)}
                    </span>
                    {mention.detail && (
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground-muted">
                        {mention.detail}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {mention.subtitle ? (
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="flex min-w-0 items-center gap-1.5 text-foreground">
                          <span className="truncate">{mention.name}</span>
                          {mention.isOnLittlebird && <LittlebirdMark />}
                        </span>
                        <span
                          className="truncate text-xs text-foreground-subtle"
                          title={mention.subtitle}
                        >
                          {mention.subtitle}
                        </span>
                      </span>
                    ) : (
                      <>
                        <span className="flex-shrink-0 truncate text-foreground">
                          {mention.name}
                        </span>
                        {mention.isOnLittlebird && <LittlebirdMark />}
                        {mention.detail && (
                          <span className="min-w-0 flex-1 truncate text-foreground-subtle">
                            — {mention.detail}
                          </span>
                        )}
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );
});

MentionSuggestionList.displayName = 'MentionSuggestionList';

const renderMentionSuggestion = (
  menuProps: Record<string, unknown> | undefined,
  variant: PickerVariant
) => {
  let component: ReactRenderer<
    SuggestionListHandle,
    SuggestionListProps
  > | null = null;
  const listProps = (
    props: SuggestionProps<MentionSuggestionItem, MentionSuggestionItem>
  ): SuggestionListProps => ({
    items: props.items,
    command: props.command,
    // The `/` menu spans the editor's width but keeps the caret's vertical
    // position: a long draft scrolls inside the composer, so the editor's own
    // edges can sit off-screen while the caret never does
    clientRect:
      variant === 'slash'
        ? () => {
            const caretRect = props.clientRect?.();
            if (!caretRect) return null;
            const editorRect = props.editor.view.dom.getBoundingClientRect();
            return new DOMRect(
              editorRect.x,
              caretRect.y,
              editorRect.width,
              caretRect.height
            );
          }
        : props.clientRect,
    menuProps,
    query: props.query,
    variant,
  });
  return {
    onStart: (
      props: SuggestionProps<MentionSuggestionItem, MentionSuggestionItem>
    ) => {
      component = new ReactRenderer(MentionSuggestionList, {
        props: listProps(props),
        editor: props.editor,
      });
    },
    onUpdate: (
      props: SuggestionProps<MentionSuggestionItem, MentionSuggestionItem>
    ) => {
      component?.updateProps(listProps(props));
    },
    onKeyDown: (props: SuggestionKeyDownProps) =>
      component?.ref?.onKeyDown(props) ?? false,
    onExit: () => {
      component?.destroy();
      component = null;
    },
  };
};

const listSourceItems = async (
  source: MentionSource,
  editor: Editor
): Promise<MentionSuggestionItem[]> => {
  try {
    return (await source.listItems(editor)).map((mention) => ({
      mention,
      source,
    }));
  } catch (error) {
    log.warn('A mention source could not list its items', {
      kind: source.kind,
      error: error instanceof Error ? error.message : String(error),
    });
    return [];
  }
};

const listSuggestionItems = (sources: MentionSource[], editor: Editor) =>
  Promise.all(sources.map((source) => listSourceItems(source, editor))).then(
    (sourceItems) => sourceItems.flat()
  );

const matchesQuery = ({ mention }: MentionSuggestionItem, query: string) => {
  const normalizedQuery = query.toLowerCase();
  return (
    normalizedQuery === '' ||
    mention.name.toLowerCase().includes(normalizedQuery) ||
    (mention.detail?.toLowerCase().includes(normalizedQuery) ?? false) ||
    (mention.isSubtitleSearchable !== false &&
      (mention.subtitle?.toLowerCase().includes(normalizedQuery) ?? false))
  );
};

const NAME_PREFIX_MATCH_RANK = 0;
const NAME_MATCH_RANK = 1;
const OTHER_FIELD_MATCH_RANK = 2;

const queryMatchRank = ({ mention }: MentionSuggestionItem, query: string) => {
  const normalizedQuery = query.toLowerCase();
  const names = [mention.name, mention.label ?? mention.name].map((name) =>
    name.toLowerCase()
  );
  if (names.some((name) => name.startsWith(normalizedQuery)))
    return NAME_PREFIX_MATCH_RANK;
  if (names.some((name) => name.includes(normalizedQuery)))
    return NAME_MATCH_RANK;
  return OTHER_FIELD_MATCH_RANK;
};

/**
 * Orders matches so a name hit beats a hit in the description. Sections keep
 * their items adjacent and are ordered by their best match.
 */
const rankByQuery = (items: MentionSuggestionItem[], query: string) => {
  const rankedItems = sortBy(items, (item) => queryMatchRank(item, query));
  const sectionOrder = uniq(rankedItems.map(sectionTitleOf));
  return sortBy(rankedItems, (item) =>
    sectionOrder.indexOf(sectionTitleOf(item))
  );
};

const capItemsPerSource = (items: MentionSuggestionItem[]) => {
  const shownCounts = new Map<MentionSource, number>();
  return items.filter(({ source }) => {
    if (source.maxItems === undefined) return true;
    const shownCount = shownCounts.get(source) ?? 0;
    shownCounts.set(source, shownCount + 1);
    return shownCount < source.maxItems;
  });
};

export const createMentionSuggestion = ({
  sources,
  menuProps,
}: MentionSuggestionOptions) => {
  return Extension.create({
    name: 'mentionSuggestion',
    priority: 200,
    addProseMirrorPlugins() {
      let itemsPromise: Promise<MentionSuggestionItem[]> | null = null;
      return [
        Suggestion<MentionSuggestionItem, MentionSuggestionItem>({
          editor: this.editor,
          char: '@',
          allowSpaces: false,
          allowToIncludeChar: true,
          items: async ({ query, editor }) => {
            const attachedMentionKeys = new Set<string>();
            editor.state.doc.descendants((node) => {
              if (node.type.name !== Mention.name) return;
              attachedMentionKeys.add(`${node.attrs.kind}:${node.attrs.id}`);
            });
            const items = await (itemsPromise ??= listSuggestionItems(
              sources,
              editor
            ));
            return capItemsPerSource(
              items.filter(
                (item) =>
                  !attachedMentionKeys.has(
                    `${item.mention.kind}:${item.mention.id}`
                  ) && matchesQuery(item, query)
              )
            );
          },
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                { type: Mention.name, attrs: { ...props.mention } },
                mentionTrailingSpace,
              ])
              .run();
            props.source.onSelect?.(props.mention, editor);
          },
          render: () => {
            const renderer = renderMentionSuggestion(menuProps, 'mention');
            return {
              ...renderer,
              onExit: () => {
                renderer.onExit();
                itemsPromise = null;
              },
            };
          },
        }),
      ];
    },
  });
};

/** TipTap extensions that add generic `@` mentions to a composer editor. */
export const createMentionExtensions = (options: MentionSuggestionOptions) => [
  Mention.configure({ sources: options.sources }),
  MentionGap,
  MentionKeymap,
  createMentionSuggestion(options),
];

/**
 * `/` picker that inserts the pick as plain `/name ` text. A slash item is a
 * command the user could have typed, not an attachment, so unlike `@` there is
 * no chip and no structured mention. Standalone: works without
 * `createMentionExtensions`.
 */
export const createSlashExtension = ({
  sources,
  menuProps,
}: MentionSuggestionOptions) => {
  return Extension.create({
    name: 'slashSuggestion',
    priority: 200,
    addProseMirrorPlugins() {
      let itemsPromise: Promise<MentionSuggestionItem[]> | null = null;
      return [
        Suggestion<MentionSuggestionItem, MentionSuggestionItem>({
          editor: this.editor,
          char: '/',
          // Suggestion's default plugin key is shared with the `@` picker
          pluginKey: new PluginKey('slashSuggestion'),
          allowSpaces: false,
          items: async ({ query, editor }) =>
            rankByQuery(
              (
                await (itemsPromise ??= listSuggestionItems(sources, editor))
              ).filter((item) => matchesQuery(item, query)),
              query
            ),
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .insertContentAt(range, `/${props.mention.name} `)
              .run();
            props.source.onSelect?.(props.mention, editor);
          },
          render: () => {
            const renderer = renderMentionSuggestion(menuProps, 'slash');
            return {
              ...renderer,
              onExit: () => {
                renderer.onExit();
                itemsPromise = null;
              },
            };
          },
        }),
      ];
    },
  });
};

const EMOJI_KIND = 'emoji';
const EMOJI_MIN_QUERY_LENGTH = 2;
const EMOJI_MAX_RESULTS = 8;
const EMOJI_QUERY = /^[a-z0-9_+-]+$/i;
const COMPLETED_SHORTCODE = /(?:^|\s)(:[a-z0-9_+-]+:)$/i;

// An emoji row uses the character itself as its id
const emojiSource: MentionSource = {
  kind: EMOJI_KIND,
  title: 'Emoji',
  listItems: async () => [],
  renderIcon: (mention) => (
    <span className="w-5 flex-shrink-0 text-center">{mention.id}</span>
  ),
};

const emojiTitle = (shortcodeName: string): string =>
  shortcodeName.split('_').map(upperFirst).join(' ');

const listEmojiItems = (query: string): MentionSuggestionItem[] => {
  const isSearchable =
    query.length >= EMOJI_MIN_QUERY_LENGTH && EMOJI_QUERY.test(query);
  if (!isSearchable) return [];
  return searchEmoji(query, EMOJI_MAX_RESULTS).map(({ emoji, name }) => ({
    mention: { id: emoji, kind: EMOJI_KIND, name: emojiTitle(name) },
    source: emojiSource,
  }));
};

/** `:` picker that inserts plain emoji characters; a fully typed `:sob:` converts on its closing colon */
export const createEmojiExtension = ({
  menuProps,
}: Pick<MentionSuggestionOptions, 'menuProps'> = {}) =>
  Extension.create({
    name: 'emojiSuggestion',
    priority: 200,
    addInputRules() {
      return [
        new InputRule({
          find: COMPLETED_SHORTCODE,
          handler: ({ state, range, match }) => {
            const shortcode = match[1];
            const normalizedShortcode = shortcode.toLowerCase();
            const emoji = emojiFromShortcodes(normalizedShortcode);
            if (emoji === normalizedShortcode) return null;
            const leadingWhitespaceLength = match[0].length - shortcode.length;
            state.tr.insertText(
              emoji,
              range.from + leadingWhitespaceLength,
              range.to
            );
          },
        }),
      ];
    },
    addProseMirrorPlugins() {
      return [
        Suggestion<MentionSuggestionItem, MentionSuggestionItem>({
          editor: this.editor,
          char: ':',
          pluginKey: new PluginKey('emojiSuggestion'),
          allowSpaces: false,
          // An active suggestion consumes Escape and Enter, so it is active only while it has rows
          allow: ({ state, range }) => {
            const $from = state.doc.resolve(range.from);
            const isInCode =
              $from.parent.type.spec.code ||
              $from.nodeAfter?.marks.some((mark) => mark.type.spec.code);
            if (isInCode) return false;
            const query = state.doc.textBetween(range.from + 1, range.to);
            return listEmojiItems(query).length > 0;
          },
          items: ({ query }) => listEmojiItems(query),
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .insertContentAt(range, `${props.mention.id} `)
              .run();
          },
          render: () => renderMentionSuggestion(menuProps, 'mention'),
        }),
      ];
    },
  });
